import LockIcon from "@uui/Lock03";

import { Chip, styled } from "@mui/material";

import IconHeading from "@app/analytics/components/IconHeading";
import { NoteSectionHeader } from "@app/analytics/components/reports/styles";
import EditedChip from "@app/analytics/MeetingReport/components/EditedChip";
import { EDITING_TOOLTIP } from "@app/analytics/MeetingReport/constants";
import useMeetingReportSessionNotes from "@app/analytics/MeetingReport/hooks/useMeetingReportSessionNotes";
import useReportActions from "@app/analytics/MeetingReport/hooks/useReportActions";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import { DisabledOverlay } from "@app/analytics/MeetingReport/styles";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import type { NoteItemOrChapter } from "@app/analytics/MeetingReport/types";
import { isChapter, isNote } from "@app/analytics/MeetingReport/utils";
import {
  getMeetingSummaryCopyContent,
  getNoteSectionCopyContent,
} from "@app/analytics/MeetingReport/utils/transcriptUtils";

import { Row } from "@rui/Stack";

import ChaptersAndTopics from "./ChaptersAndTopics/ChaptersAndTopics";
import CopyAndEditActionsRow from "./CopyAndEditActionsRow";
import MeetingSummary from "./MeetingSummary";
import NoteCard from "./NoteCard";
import NoteSubSection from "./NoteSubSection";
import { NoteSectionHeaderTitleAndActionsContainer } from "./styles";

interface MeetingReportRecapNotesProps {
  inMobileView: boolean;
  isNotetakerReport: boolean;
  isRestrictedReport: boolean;
  shouldUpsell: boolean;
}

const PrivateChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.shades.neutral[400],
  fontWeight: 500,
  borderRadius: "4px",
  backgroundColor: theme.palette.shades.neutral[50],
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "12px",
  height: "fit-content",
  "& .MuiChip-label": {
    padding: theme.spacing(0, 0.5),
    [theme.breakpoints.down("mobile")]: {
      display: "none",
    },
  },
  "& .MuiChip-icon": {
    color: theme.palette.shades.neutral[500],
    margin: "4px",
    fontSize: "16px",
    [theme.breakpoints.up("mobile")]: {
      display: "none",
    },
  },
  [theme.breakpoints.up("mobile")]: {
    height: "20px",
  },
}));

const logItemClick = (item: NoteItemOrChapter) => {
  if (isChapter(item)) {
    return () => Events.playChapter({ id: item.id, label: item.title });
  }

  if (isNote(item)) {
    return Events.playNote;
  }

  return () => Events.playHighlight({ id: item.id, type: item.type! });
};

const MeetingReportRecapNotes = ({
  inMobileView,
  isNotetakerReport,
  isRestrictedReport,
  shouldUpsell,
}: MeetingReportRecapNotesProps) => {
  const { summary } = useTranscript({
    enabled: !isRestrictedReport && !isNotetakerReport,
  });
  const { notes: sessionNotes, isNotesEdited } = useMeetingReportSessionNotes({
    enabled: !isRestrictedReport,
  });
  const { shouldJumpToTimestamp, makeJumpToTimestamp, makeScrollToItem } =
    useReportActions();

  const { editAccessLevel, isEditing, isEditingItem } = useEditStore(
    (state) => ({
      editAccessLevel: state.editAccessLevel,
      isEditing: state.editingSection === "notes",
      isEditingItem: state.notes.activeItem != null,
    })
  );

  const searchResultsByType = useSearchStore(
    (state) => state.searchResultsByNoteTypeAndId
  );

  const jumpToSummaryItem = (
    item: NoteItemOrChapter
  ): (() => void) | undefined => {
    if (shouldJumpToTimestamp && item.relativeStartTime) {
      return makeJumpToTimestamp(item.relativeStartTime, {
        onJumpTo: logItemClick(item),
      });
    }

    if (isChapter(item)) {
      return makeScrollToItem({ type: "chapter", id: item.id });
    }

    return makeScrollToItem({
      type: "paragraph",
      rawTimestamp: item.startTime?.toMillis(),
    });
  };

  const hasFullEditAccess = editAccessLevel === "full";
  const disableEditing = isEditing && !hasFullEditAccess;

  const editingDisabledTooltipTitle = disableEditing
    ? EDITING_TOOLTIP.cannotEditSection
    : undefined;

  const summaryDisabled = isEditing && !summary.summary.value;

  const summaryDisabledTooltip = summaryDisabled
    ? EDITING_TOOLTIP.nothingToEditInSection
    : editingDisabledTooltipTitle;

  const summaryEditingDisabled = disableEditing || summaryDisabled;

  return (
    <>
      <DisabledOverlay
        variant="light"
        className={isEditingItem ? undefined : "hidden"}
      />
      {!isNotetakerReport && (
        <NoteCard
          forceExpand={searchResultsByType.summary.size > 0}
          isEditing={isEditing}
          header={
            <NoteSectionHeader variant="apart">
              <NoteSectionHeaderTitleAndActionsContainer>
                <IconHeading type="summary">Summary</IconHeading>
                <CopyAndEditActionsRow
                  isEditing={isEditing}
                  canEdit={hasFullEditAccess}
                  canCopy={Boolean(summary.summary.value)}
                  copyValue={getMeetingSummaryCopyContent({
                    summary: summary.summary,
                  })}
                  name="summary"
                />
              </NoteSectionHeaderTitleAndActionsContainer>
              {summary.editing.isSummaryEdited && !inMobileView && (
                <EditedChip />
              )}
            </NoteSectionHeader>
          }
          wrapperProps={{
            disabled: summaryEditingDisabled,
            disabledTooltip: summaryDisabledTooltip,
            sx: { mt: 0 },
          }}
        >
          <MeetingSummary
            forceExpand={inMobileView || summaryEditingDisabled}
            summary={summary.summary}
            shouldUpsell={shouldUpsell}
          />
          {summary.editing.isSummaryEdited && inMobileView && <EditedChip />}
        </NoteCard>
      )}
      <ChaptersAndTopics
        forceExpand={
          searchResultsByType.chapter.size > 0 ||
          searchResultsByType.topic.size > 0
        }
        isEdited={
          summary.editing.isChaptersEdited || summary.editing.isTopicsEdited
        }
        canEdit={hasFullEditAccess}
        disableEditing={disableEditing}
        isNotetakerReport={isNotetakerReport}
        onItemClick={jumpToSummaryItem}
        shouldUpsell={shouldUpsell}
        tooltip={editingDisabledTooltipTitle}
        topics={summary.topics}
      />
      <NoteCard
        forceExpand={searchResultsByType.action_item.size > 0}
        isEditing={isEditing}
        header={
          <NoteSectionHeader variant="apart">
            <NoteSectionHeaderTitleAndActionsContainer>
              <IconHeading type="action_items">Action Items</IconHeading>
              <CopyAndEditActionsRow
                name="action-items"
                isEditing={isEditing}
                canEdit={hasFullEditAccess}
                canCopy={Boolean(summary.actionItems.length)}
                copyValue={getNoteSectionCopyContent({
                  name: "Action Items",
                  data: summary.actionItems,
                })}
              />
            </NoteSectionHeaderTitleAndActionsContainer>
            {summary.editing.isActionItemsEdited && !inMobileView && (
              <EditedChip />
            )}
          </NoteSectionHeader>
        }
        wrapperProps={{
          disabled: disableEditing,
          disabledTooltip: editingDisabledTooltipTitle,
        }}
      >
        <NoteSubSection
          type="action_item"
          items={summary.actionItems}
          isNotetakerReport={isNotetakerReport}
          onItemClick={jumpToSummaryItem}
          shouldUpsell={shouldUpsell}
          maxItems={inMobileView ? summary.actionItems.length : undefined}
        />
        {summary.editing.isActionItemsEdited && inMobileView && <EditedChip />}
      </NoteCard>
      <NoteCard
        forceExpand={searchResultsByType.key_question.size > 0}
        isEditing={isEditing}
        header={
          <NoteSectionHeader variant="apart">
            <NoteSectionHeaderTitleAndActionsContainer>
              <IconHeading type="key_questions">Key Questions</IconHeading>
              <CopyAndEditActionsRow
                name="key-questions"
                isEditing={isEditing}
                canEdit={hasFullEditAccess}
                canCopy={Boolean(summary.keyQuestions.length)}
                copyValue={getNoteSectionCopyContent({
                  name: "Key Questions",
                  data: summary.keyQuestions,
                })}
              />
            </NoteSectionHeaderTitleAndActionsContainer>
            {summary.editing.isKeyQuestionsEdited && !inMobileView && (
              <EditedChip />
            )}
          </NoteSectionHeader>
        }
        wrapperProps={{
          disabled: disableEditing,
          disabledTooltip: editingDisabledTooltipTitle,
        }}
      >
        <NoteSubSection
          type="key_question"
          items={summary.keyQuestions}
          isNotetakerReport={isNotetakerReport}
          onItemClick={jumpToSummaryItem}
          shouldUpsell={shouldUpsell}
          maxItems={inMobileView ? summary.keyQuestions.length : undefined}
        />
        {summary.editing.isKeyQuestionsEdited && inMobileView && <EditedChip />}
      </NoteCard>
      {!isRestrictedReport && (
        <NoteCard
          forceExpand={searchResultsByType.note.size > 0}
          header={
            <NoteSectionHeader>
              <Row variant="apart" spacing={1} width="100%">
                <NoteSectionHeaderTitleAndActionsContainer>
                  <IconHeading type="notes">
                    {isNotetakerReport ? "Notepad" : "Your Notes"}
                  </IconHeading>
                  <CopyAndEditActionsRow
                    isEditing={isEditing}
                    canEdit={editAccessLevel !== "none"}
                    canCopy={Boolean(sessionNotes.length)}
                    copyValue={getNoteSectionCopyContent({
                      name: "Notes",
                      data: sessionNotes,
                    })}
                    name="session-notes"
                  />
                </NoteSectionHeaderTitleAndActionsContainer>
                <Row variant="right" spacing={1}>
                  {!isNotetakerReport && (
                    <PrivateChip
                      label="Only you can see this"
                      icon={<LockIcon />}
                    />
                  )}
                  {isNotesEdited && !inMobileView && <EditedChip />}
                </Row>
              </Row>
            </NoteSectionHeader>
          }
          isEditing={isEditing}
        >
          <NoteSubSection
            type="note"
            items={sessionNotes}
            isNotetakerReport={isNotetakerReport}
            onItemClick={jumpToSummaryItem}
            shouldUpsell={shouldUpsell}
            maxItems={inMobileView ? sessionNotes.length : undefined}
          />
          {isNotesEdited && inMobileView && <EditedChip />}
        </NoteCard>
      )}
    </>
  );
};

export default MeetingReportRecapNotes;
